import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

const demoSteps = [
  {
    route: '/dashboard',
    highlightSelector: '[data-testid="active-investigation-card"]',
    title: 'Step 1 of 5',
    text: 'An integrity warning was detected across 8,203 AI decisions in the Legal Document Drafting workflow. This creates potential SOC 2 PI1 and EU AI Act Annex IV exposure. This is what Apex surfaces before your auditor does.',
    button: 'See the investigation',
  },
  {
    route: '/dashboard/incident-review',
    highlightSelector: '[data-testid="investigation-timeline"]',
    title: 'Step 2 of 5',
    text: 'From first signal to response package: 3 minutes, 47 seconds. Every step logged, timestamped, and auditable.',
    button: 'See the decision record',
  },
  {
    route: '/dashboard/decision-ledger/DER-2026-031204',
    highlightSelector: '[data-testid="decision-significance"]',
    fallbackSelector: '[data-testid="risk-classification"]',
    title: 'Step 3 of 5',
    text: 'This is one of the 8,203 affected decisions. Evidence incomplete. Integrity warning detected. Owner assigned. SLA clock running.',
    button: 'See the blast radius',
  },
  {
    route: '/dashboard/decision-ledger/DER-2026-031204',
    highlightSelector: '[data-testid="related-records"]',
    scrollTarget: true,
    title: 'Step 4 of 5',
    text: '143 decisions share the upstream source. 3 audit packages may contain affected evidence. Last clean state: April 3rd.',
    button: 'See the resolution',
  },
  {
    route: '/dashboard/decision-ledger/DER-2026-031204',
    highlightSelector: '[data-testid="resolution-lifecycle"]',
    scrollTarget: true,
    title: 'Step 5 of 5',
    text: 'Detected. Investigating. Remediation in progress. Apex closes the loop \u2014 detection to closure, fully documented.',
    button: null,
  },
];

export default function DemoTour({ onEnd }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [cardPos, setCardPos] = useState('bottom');
  const retryRef = useRef(null);

  const current = demoSteps[step];

  const findAndHighlight = useCallback(() => {
    if (!current || isComplete) return;
    const tryFind = () => {
      let el = document.querySelector(current.highlightSelector);
      if (!el && current.fallbackSelector) el = document.querySelector(current.fallbackSelector);
      if (el) {
        // Scroll into view within Radix viewport
        const sv = document.querySelector('[data-radix-scroll-area-viewport]');
        if (sv && current.scrollTarget) {
          const elTop = el.getBoundingClientRect().top + sv.scrollTop - sv.getBoundingClientRect().top;
          sv.scrollTop = Math.max(0, elTop - 100);
        }
        setTimeout(() => {
          const rect = el.getBoundingClientRect();
          setHighlightRect({ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 });
          setCardPos(rect.top > window.innerHeight * 0.5 ? 'top' : 'bottom');
        }, 200);
        if (retryRef.current) clearInterval(retryRef.current);
        return true;
      }
      return false;
    };
    if (!tryFind()) {
      let attempts = 0;
      retryRef.current = setInterval(() => {
        attempts++;
        if (tryFind() || attempts > 20) clearInterval(retryRef.current);
      }, 200);
    }
  }, [current, isComplete]);

  useEffect(() => {
    if (isComplete || !current) return;
    setTransitioning(true);
    setHighlightRect(null);
    if (location.pathname !== current.route) navigate(current.route);
    const timer = setTimeout(() => {
      findAndHighlight();
      setTransitioning(false);
    }, 800);
    return () => { clearTimeout(timer); if (retryRef.current) clearInterval(retryRef.current); };
  }, [step, isComplete, current, navigate, location.pathname, findAndHighlight]);

  const goNext = () => {
    if (step < demoSteps.length - 1) setStep(s => s + 1);
    else setIsComplete(true);
  };

  if (isComplete) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="demo-completion">
        <div className="absolute inset-0 bg-[#080D1A]" />
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(99,102,241,0.15), transparent 60%), radial-gradient(ellipse at 70% 50%, rgba(99,102,241,0.1), transparent 60%)' }} />
        <div className="relative z-10 text-center max-w-2xl mx-auto px-6">
          <p className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] leading-snug mb-10" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            When AI becomes a board-level event,<br />Apex proves you were in control.
          </p>
          <div className="flex items-center justify-center gap-4 mb-8 flex-wrap">
            <button data-testid="demo-explore" onClick={() => { navigate('/dashboard'); onEnd(); }} className="inline-flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-[0_0_30px_rgba(99,102,241,0.3)]">
              Explore platform <ArrowRight size={18} />
            </button>
            <button data-testid="demo-clean-record" onClick={() => { navigate('/dashboard/decision-ledger/DER-2026-047832'); onEnd(); }} className="inline-flex items-center gap-2 border-2 border-white/80 hover:border-white text-white font-semibold px-8 py-3.5 rounded-xl transition-all hover:bg-white/5">
              See a clean record <ArrowRight size={18} />
            </button>
          </div>
          <p className="text-sm text-[#475569]">19 screens · Full platform · All data is simulated for demonstration</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9998]" data-testid="demo-tour-overlay">
      {!highlightRect && <div className="absolute inset-0 bg-black/70" />}
      {highlightRect && (
        <div className="absolute pointer-events-none" style={{
          top: highlightRect.top, left: highlightRect.left,
          width: highlightRect.width, height: highlightRect.height,
          borderRadius: 12,
          boxShadow: '0 0 0 3px #6366F1, 0 0 20px rgba(99,102,241,0.4), 0 0 0 9999px rgba(0,0,0,0.7)',
          animation: 'tourPulse 2s ease-in-out infinite',
          zIndex: 9998,
        }} />
      )}
      <div className={`fixed left-1/2 -translate-x-1/2 w-[520px] max-w-[calc(100vw-32px)] z-[9999] transition-all duration-300 ${transitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}
        style={cardPos === 'bottom' ? { bottom: 32 } : { top: 32 }} data-testid="demo-card">
        <div className="bg-[#0F1629] border-2 border-[#6366F1]/60 rounded-2xl p-6 shadow-2xl shadow-[#6366F1]/10">
          <button data-testid="demo-exit" onClick={onEnd} className="absolute top-4 right-4 text-xs text-[#475569] hover:text-[#94A3B8] transition-colors flex items-center gap-1">
            Exit tour <X size={14} />
          </button>
          <div className="text-[10px] text-[#6366F1] font-semibold uppercase tracking-wider mb-2">{current?.title}</div>
          <p className="text-sm text-[#94A3B8] leading-relaxed mb-5">{current?.text}</p>
          {current?.button ? (
            <button data-testid="demo-next" onClick={goNext} className="flex items-center gap-1 bg-[#6366F1] hover:bg-[#818cf8] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)]">
              {current.button} <ArrowRight size={16} />
            </button>
          ) : (
            <button data-testid="demo-finish" onClick={goNext} className="flex items-center gap-1 bg-[#6366F1] hover:bg-[#818cf8] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)]">
              Complete demo <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
